import { useMemo } from 'react';
import { useQuotaCustomers } from '../../api/customerOwnership.ts';
import { useGuaranteedServiceList, useGuaranteedServiceTextSkus } from '../../api/guaranteedService.ts';
import { useTargetProbeLegs } from '../../api/useTargetProbes.ts';

export interface PhysicalLegName {
  readonly upstream_name: string;
  readonly physical_model: string;
}

export interface OrgName {
  readonly org_name: string;
  readonly email: string;
}

export interface GuaranteedNameMaps {
  readonly skuNameById: ReadonlyMap<string, string>;
  readonly legByPhysicalKey: ReadonlyMap<string, PhysicalLegName>;
  readonly orgNameById: ReadonlyMap<string, OrgName>;
  readonly skuOptions: readonly { readonly value: string; readonly label: string }[];
  readonly upstreamOptions: readonly { readonly value: string; readonly label: string }[];
}

/** upstream × account × bucket × channel. Null when any id is missing. */
export function physicalLegKey(parts: {
  readonly upstream_id: string | null;
  readonly account_id: string | null;
  readonly key_bucket_id: string | null;
  readonly upstream_channel_id: string | null;
}): string | null {
  if (
    parts.upstream_id === null
    || parts.account_id === null
    || parts.key_bucket_id === null
    || parts.upstream_channel_id === null
  ) return null;
  return [
    parts.upstream_id,
    parts.account_id,
    parts.key_bucket_id,
    parts.upstream_channel_id,
  ].join('\u0000');
}

/** One pass over entries, legs, and customers. Rows look up names from these maps. */
export function useGuaranteedNameMaps(): GuaranteedNameMaps {
  const list = useGuaranteedServiceList();
  const textSkus = useGuaranteedServiceTextSkus();
  const legs = useTargetProbeLegs();
  const customers = useQuotaCustomers();
  return useMemo(() => {
    // Configured SKUs first so the filter dropdown leads with them; every
    // text SKU after that so an event on a since-removed SKU still shows a name.
    const skuNameById = new Map<string, string>();
    for (const row of list.data?.sku ?? []) skuNameById.set(row.sku_id, row.sku_name);
    for (const row of list.data?.org_sku ?? []) {
      if (!skuNameById.has(row.sku_id)) skuNameById.set(row.sku_id, row.sku_name);
    }
    for (const row of textSkus.data?.skus ?? []) {
      if (!skuNameById.has(row.sku_id)) skuNameById.set(row.sku_id, row.sku_name);
    }
    const legByPhysicalKey = new Map<string, PhysicalLegName>();
    const upstreams = new Map<string, string>();
    for (const leg of legs.data?.legs ?? []) {
      const key = physicalLegKey(leg);
      if (key !== null && !legByPhysicalKey.has(key)) {
        legByPhysicalKey.set(key, {
          upstream_name: leg.upstream_name,
          physical_model: leg.physical_model,
        });
      }
      if (!upstreams.has(leg.upstream_id)) upstreams.set(leg.upstream_id, leg.upstream_name);
    }
    const orgNameById = new Map<string, OrgName>();
    for (const row of customers.data?.customers ?? []) {
      orgNameById.set(row.org_id, { org_name: row.org_name, email: row.email });
    }
    return {
      skuNameById,
      legByPhysicalKey,
      orgNameById,
      skuOptions: [...skuNameById].map(([value, label]) => ({ value, label })),
      upstreamOptions: [...upstreams].map(([value, label]) => ({ value, label })),
    };
  }, [list.data, textSkus.data, legs.data, customers.data]);
}
